import { useEffect } from "react";

import {
  BracketsCurly,
  Code,
  TextB,
  TextItalic,
  TextStrikethrough,
} from "@phosphor-icons/react";

import {
  Extension,
  useEditor,
} from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";

import {
  InputText,
  InputTextAreaView,
  Toolbar,
} from "./styles";

import Link from "@tiptap/extension-link";
import { ChannelInputRules } from "../../../../utils/channelInputRules";
import { parseHtmlToChannelMarkup, parseChannelMarkupToHtml } from "../../../../utils/channelAndHtml";
import { VariableMark } from "../../../../utils/variableMark";
import { VariableInsertCommand } from "../../../../utils/variableInsertCommand";
import { VariableKeyboardShortCut } from "../../../../utils/variableKeyboardShortCut";
import { Button, Size, Variant } from "../../../Buttons";

export interface TextMessageInputProps {
  value: string;
  onChange: ( value: string ) => void;
};

export const TextMessageInput = ({
  value,
  onChange,
}: TextMessageInputProps ) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: {
          HTMLAttributes: {
            style: "background:#38BDF821; padding:2px 4px; border-radius:4px; font-family:monospace; white-space:pre-wrap;",
          }
        },
      }),
      ChannelInputRules,
      Link.configure({
        autolink: true,
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: "Digite mensagem de texto",
      }),
      VariableMark,
      VariableInsertCommand,
      VariableKeyboardShortCut,
      Extension.create({
        name: "variableConfirmOnEnter",

        addKeyboardShortcuts() {
          return {
            Enter: ({ editor }) => {
              const { from } = editor.state.selection;

              const nodeBefore = editor.state.doc.nodeAt( from - 1 );
              const nodeAfter = editor.state.doc.nodeAt( from );

              const isInsideVariable =
                nodeBefore?.marks?.some(mark => mark.type.name === "variable") ||
                nodeAfter?.marks?.some(mark => mark.type.name === "variable");

              if ( isInsideVariable ) {
                const resolvedPos = editor.state.doc.resolve( from );
                const nextPos = resolvedPos.nodeAfter ? from + resolvedPos.nodeAfter.nodeSize : from;

                editor.chain().focus()
                  .setTextSelection( nextPos )
                  .unsetMark("variable")
                  .run();

                return true;
              }

              return false;
            },
          }
        },
      })
    ],
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-label": "Conteúdo da mensagem",
        "aria-multiline": "true",
      },
    },
    content: parseChannelMarkupToHtml( value ),
    onUpdate: ({ editor }) => onChange( parseHtmlToChannelMarkup( editor.getHTML())),
  });

  useEffect(() => {
    if( !editor ) return;

    const currentValue = parseHtmlToChannelMarkup( editor.getHTML());

    if( currentValue !== value ) {
      editor.commands.setContent( parseChannelMarkupToHtml( value ), false );
    }
  }, [ editor, value ]);

  return (
    <InputTextAreaView>
      <Toolbar role="toolbar" aria-label="Formatação da mensagem">
        <Button
          $color={ editor?.isActive("bold") ? "info" : "text"}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          $variant={ Variant.OUTLINED }
          $size={ Size.VERY_SMALL }
          aria-label="Negrito"
          aria-pressed={ Boolean( editor?.isActive("bold"))}
        >
          <TextB
            size={ 16 }
          />
        </Button>

        <Button
          $color={ editor?.isActive("italic") ? "info" : "text"}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          $variant={ Variant.OUTLINED }
          $size={ Size.VERY_SMALL }
          aria-label="Itálico"
          aria-pressed={ Boolean( editor?.isActive("italic"))}
        >
          <TextItalic
            size={ 16 }
          />
        </Button>

        <Button
          $color={ editor?.isActive("strike") ? "info" : "text"}
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          $variant={ Variant.OUTLINED }
          $size={ Size.VERY_SMALL }
          aria-label="Tachado"
          aria-pressed={ Boolean( editor?.isActive("strike"))}
        >
          <TextStrikethrough
            size={ 16 }
          />
        </Button>

        <Button
          $color={ editor?.isActive("code") ? "info" : "text"}
          onClick={() => editor?.chain().focus().toggleCode().run()}
          $variant={ Variant.OUTLINED }
          $size={ Size.VERY_SMALL }
          aria-label="Código"
          aria-pressed={ Boolean( editor?.isActive("code"))}
        >
          <Code
            size={ 16 }
          />
        </Button>

        <Button
          $color={editor?.isActive("variable") ? "info" : "text"}
          onClick={() => editor?.commands.insertVariable()}
          $variant={ Variant.OUTLINED }
          $size={ Size.VERY_SMALL }
          aria-label="Inserir variável"
          aria-pressed={ Boolean( editor?.isActive("variable"))}
        >
          <BracketsCurly
            size={ 16 }
          />
        </Button>
      </Toolbar>

      <InputText
        editor={ editor }
      />
    </InputTextAreaView>
  );
};
